import {
  Grid,
  InputLabel,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';
import {
  Button,
  IconButton,
  GreenSwitch,
  useConfirmation,
} from '@coolremote/shared';
import clsx from 'clsx';
import _ from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import CircularSlider from 'react-circular-slider-svg';
import { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import { BsGraphUp } from 'react-icons/bs';
import { jt, t } from 'ttag';
import { symbols } from '../../constants/sensorsUnitsSymbols';
import { useStoreActions, useStoreState } from '../../models/RootStore';
import {
  isEndStampLaterThanStartStamp,
  minsToTime,
  stringTimeToUTCMins,
} from '../../services/timeService';
import { Checkbox } from '../Checkbox';
import addEditScheduleStyles from './addEditSchedule.style';
import useChurnZero from '@hooks/useChurnZero';
import TimePickerRow from './components/TimePickerRow';
import { sensorMeasurementUnits } from '@models/Sensors';
import CustomCheckbox from './components/CustomCheckbox';
import SetpointPicker from './components/SetpointPicker';
import ScheduleSummary from './components/ScheduleSummary';
import CheckboxRow from './components/CheckboxRow';
import FanSpeedPicker from './components/FanSpeedPicker';
import DaysDatePicker from './components/DaysDatePicker';
import ScheduleWrapper from './components/ScheduleWrapper';
import ScheduleNameRow from './components/ScheduleNameRow';
import {
  getScheduleMappingIssues,
  getScheduleRequiredProperties,
  UNIVERSAL_PROPERTIES,
  UniversalGroupMember,
  UniversalProperty,
} from '../../utils/universalUnit';

const useStyles = makeStyles(addEditScheduleStyles);

const getPropertyLabel = (property: UniversalProperty) => {
  if (property === UNIVERSAL_PROPERTIES.OPERATION_STATUS) {
    return t`On/Off`;
  }
  if (property === UNIVERSAL_PROPERTIES.SETPOINT) {
    return t`Setpoint`;
  }
  if (property === UNIVERSAL_PROPERTIES.FAN_MODE) {
    return t`Fan Speed`;
  }
  return property;
};

const hasValue = (value: any) => !!value || value === 0;
const hasTime = (time: any) => time !== '' && time !== null && time >= 0;

const isEqual = (arr1: any, arr2: any) => {
  if (arr1?.length !== arr2?.length) {
    return false;
  }

  let equal = true;
  arr1.forEach((day: string, index: number) => {
    if (!equal) {
      return;
    }

    if (!arr2.includes(day)) {
      equal = false;
      return;
    }

    if (!arr1.includes(arr2[index])) {
      equal = false;
      return;
    }
  });
  return equal;
};

const allFanModes = [0, 1, 2, 3, 4, 5];

/**
 * Unified schedule editor for both unit/group schedules and sensor schedules.
 * Pass a `sensor` prop to enable sensor mode; omit it for unit/group mode.
 *
 * Unit/group props: addItemSchedule, itemId, isGroup, isHalfCDegreeEnabled, universalMembers (group's BACnet/Modbus units)
 * Sensor props:     sensor, addScheduleLocaly
 * Shared props:     editMode, scheduleData, hideSchedulesPanel, closeAddEditSchedule, saveScheduleData
 */
const AddEditSchedule: React.FC<any> = (props: any) => {
  const theme : any = useTheme();
  const {
    // Shared
    scheduleData = {},
    editMode,
    hideSchedulesPanel,
    closeAddEditSchedule,
    saveScheduleData,
    // Unit/group-specific
    addItemSchedule,
    itemId,
    isGroup,
    isHalfCDegreeEnabled,
    universalMembers = [],
    // Sensor-specific
    sensor,
    addScheduleLocaly,
  } = props;

  const isSensor = !!sensor;



  // ── Shared store ──────────────────────────────────────────────────────────
  const user = useStoreState((state) => state.users.me);
  const timeFormat = useStoreState((state) => state.users.timeFormat);
  const types = useStoreState((state) => state.types);
  const {
    timeFormat: timeFormatTypes,
    scheduleCategories,
    curveTempModes,
  } = types;
  const { timeFormat: myTimeFormat } = user;

  const updateSchedule = useStoreActions(
    (actions) => actions.schedules.updateSchedule
  );
  const { addMessage } = useStoreActions((action) => action.errorMessage);

  // ── Unit/group store ──────────────────────────────────────────────────────
  const temperatureSymbol = useStoreState(
    (state) => state.users.getTemperatureScaleDisplay
  );
  const unit = useStoreState((state) =>
    !isSensor ? state.units.allUnits[itemId] : null
  );
  const createUnitScheduleAPI = useStoreActions(
    (actions) => actions.schedules.createObjectSchedules
  );
  const { trackEvent } = useChurnZero();

  // ── Sensor store ──────────────────────────────────────────────────────────
  const createSensorScheduleAPI = useStoreActions(
    (actions) => actions.sensors.createSensorSchedule
  );

  // ── Derived values ────────────────────────────────────────────────────────
  const { sensorTypes: fullSensorTypes } = types;
  const {
    enums: sensorEnums,
    type: sensorType,
    userMinMax,
    valueMinMax,
    sensorUnit,
  } = sensor || {};
  const isOnOff =
    isSensor &&
    fullSensorTypes[sensorType]?.measurementUnits ===
    sensorMeasurementUnits.OpenClose;

  const { capabilityFlags = {} } = unit || {};
  const {
    enableSetpointControl = false,
    enableFanModeSelection = false,
    enableCurveTempSelection = false,
  } = capabilityFlags;

  const { temperatureScale } = user;
  const changeScale = isHalfCDegreeEnabled ? 0.5 : 1;

  const timeFormatObject = myTimeFormat
    ? timeFormatTypes[myTimeFormat]
    : timeFormatTypes[0];
  const is12Hours = timeFormatObject.text === '12 hours';
  const title = editMode ? t`Edit Schedule` : t`Add Schedule`;

  const classes = useStyles();

  // ── Shared state ──────────────────────────────────────────────────────────
  const [scheduleDisabled, setScheduleStatus] = useState<boolean>(false);
  const [powerOnTime, setPowerOnTime] = useState<string>('');
  const [scheduleTime, setScheduleTime] = useState<string>('');
  const [startTimeActive, setStartTimeActive] = useState<boolean>(true);
  const [powerOffTime, setPowerOffTime] = useState<string>('');
  const [endTimeActive, setEndTimeActive] = useState<boolean>(false);
  const [days, setDays] = useState<[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [schedule, setSchedule] = useState<any>({ name: '' });
  const confirm = useConfirmation();
  const [disableAdding, setDisableAdding] = useState<boolean>(false);
  const [scheduleName, setScheduleName] = useState<string>('');
  const [tab, setTab] = useState<number>(scheduleCategories.weekly);
  const [dates, setDates] = useState<any>([]);
  const [dirty, setDirty] = useState<boolean>(false);



  // ── Unit/group-specific state ─────────────────────────────────────────────
  const [setpoint, setSetpoint] = useState<number>(
    temperatureScale === 2 ? 75 : 24
  );
  const [setpointEnabled, enableSetpoint] = useState<boolean>(false);
  const [enableFanSpeed, setEnableFanSpeed] = useState<boolean>(false);
  const [selectedFanSpeed, setSelectedFanSpeed] = useState<any>(null);

  // ── Sensor-specific state ─────────────────────────────────────────────────
  const sensorInitialValue = isSensor
    ? sensorType === 130
      ? userMinMax
        ? (userMinMax.userMin + userMinMax.userMax) / 2
        : (valueMinMax.min + valueMinMax.max) / 2
      : 0
    : 0;
  const [value, setValue] = useState<number>(sensorInitialValue);
  const [mainValue, setMainValue] = useState<number>(sensorInitialValue);

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!scheduleTime) {
      return;
    }

    if (startTimeActive && !endTimeActive) {
      setPowerOnTime(scheduleTime);
    }
    if (!startTimeActive && endTimeActive) {
      setPowerOffTime(scheduleTime);
    }

    if (startTimeActive && endTimeActive) {
      setPowerOnTime(scheduleTime);
    }

  }, [scheduleTime, startTimeActive, endTimeActive])

  // ── Unit-only helpers ─────────────────────────────────────────────────────
  const CurveTempIcon = ({ i }: { i: number }) =>
    i ? (
      <div style={{ width: 24, height: 24, position: 'relative' }}>
        <span className={classes.curveTempIcon}>{i}</span>
        <BsGraphUp style={{ height: 24, width: 24 }} />
      </div>
    ) : (
      <div style={{ display: 'grid' }}>
        <BsGraphUp style={{ height: 24, width: 24 }} />
        <span className={classes.curveTempOffIcon}>{t`OFF`}</span>
      </div>
    );



  // ── Schedule population ───────────────────────────────────────────────────
  const passScheduleInfo = (scheduleInfo: any) => {
    const {
      name,
      powerOnTime: onTime,
      powerOffTime: offTime,
      days: scheduleDays = [],
      dates: scheduleDates = [],
      isDisabled,
      scheduleCategory,
      // Unit-specific
      setpoint: scheduleSetpoint,
      fanMode,
      // Sensor-specific
      value: scheduleValue,
    } = scheduleInfo || {};

    const powerOnTime = hasValue(onTime) ? minsToTime(onTime, timeFormat) : '';
    const powerOffTime = hasValue(offTime) ? minsToTime(offTime, timeFormat) : '';


    setScheduleStatus(isDisabled);
    setScheduleTime(powerOnTime || powerOffTime);
    setPowerOnTime(powerOnTime);
    setStartTimeActive(isSensor ? hasTime(onTime) : hasValue(onTime));
    setPowerOffTime(powerOffTime);
    setEndTimeActive(isSensor ? hasTime(offTime) : hasValue(offTime));
    setScheduleName(name || '');
    setTab(scheduleCategory);
    setDays(scheduleDays as []);
    const selectedDates = scheduleDates.map((date: any) => new Date(date));
    setDates(selectedDates);

    if (isSensor) {
      setValue(scheduleValue);
      setMainValue(scheduleValue);
    } else {
      scheduleSetpoint &&
        (isHalfCDegreeEnabled
          ? setSetpoint(Math.floor(scheduleSetpoint * 2) / 2)
          : setSetpoint(Math.round(scheduleSetpoint)));
      scheduleSetpoint && enableSetpoint(true);
      !_.isNil(fanMode) && setEnableFanSpeed(true);
      !isNaN(fanMode) && setSelectedFanSpeed(fanMode);
    }
  };

  useEffect(() => {
    if (!editMode) {
      return;
    }

    setSchedule(scheduleData);
    passScheduleInfo(scheduleData);
  }, [is12Hours]);

  // ── Validation ────────────────────────────────────────────────────────────
  const checkRequiredFields = () => {
    setErrorMsg('');

    if (!scheduleName) {
      setErrorMsg(t`Schedule Name is Required`);
      return false;
    }
    if (scheduleName.length < 3 || scheduleName.length > 25) {
      setErrorMsg(t`Schedule Name should be from 3 to 25 chars`);
      return false;
    }

    if (tab === scheduleCategories.calendar && dates.length === 0) {
      setErrorMsg(t`Pick one date at least`);
      return false;
    }
    if (tab === scheduleCategories.weekly && days.length === 0) {
      setErrorMsg(t`Pick one day at least`);
      return false;
    }

    if (!startTimeActive && !endTimeActive) {
      setErrorMsg(t`Add at least start or end time`);
      return false;
    }

    if (!powerOnTime && !powerOffTime) {
      setErrorMsg(t`Add at least start or end time`);
      return false;
    }

    if (startTimeActive && !powerOnTime) {
      setErrorMsg(t`Start time is required`);
      return false;
    }

    if (endTimeActive && !powerOffTime) {
      setErrorMsg(t`End time is required`);
      return false;
    }

    if (!(powerOnTime && powerOffTime)) {
      return true;
    }

    if (
      startTimeActive &&
      endTimeActive &&
      !isEndStampLaterThanStartStamp(powerOnTime, powerOffTime, is12Hours)
    ) {
      setErrorMsg(t`End time must be later than start time`);
      return false;
    }

    return true;
  };

  // ── Save logic ────────────────────────────────────────────────────────────
  const editSchedule = () => {
    const data: any = {
      isDisabled: scheduleDisabled,
      name: scheduleName,
      powerOnTime: startTimeActive
        ? powerOnTime === ''
          ? schedule.powerOnTime === 0
            ? undefined
            : null
          : stringTimeToUTCMins(powerOnTime, is12Hours)
        : null,
      powerOffTime: endTimeActive
        ? powerOffTime === ''
          ? schedule.powerOffTime === 0
            ? undefined
            : null
          : stringTimeToUTCMins(powerOffTime, is12Hours)
        : null,
      scheduleCategory: tab,
    };

    if (isSensor) {
      data.value = value;
      data.userMin = userMinMax
        ? userMinMax.userMin
        : hasValue(scheduleData.min)
          ? null
          : undefined;
      data.userMax = userMinMax
        ? userMinMax.userMax
        : hasValue(scheduleData.max)
          ? null
          : undefined;
      if (tab === scheduleCategories.calendar) {
        data.dates = dates;
      }
      if (tab === scheduleCategories.weekly) {
        data.days = days;
      }
    } else {
      let setpointValue: any;
      if (setpointEnabled) {
        setpointValue = setpoint;
      } else {
        setpointValue = schedule.setpoint ? null : undefined;
      }
      data.setpoint = setpointValue;
      data.fanMode = enableFanSpeed ? selectedFanSpeed : null;
      if (tab === scheduleCategories.calendar) {
        data.dates = dates;
        data.days = [];
      } else if (tab === scheduleCategories.weekly) {
        data.days = days;
        data.dates = [];
      }
    }

    updateSchedule({
      id: scheduleData.id as string,
      data,
    })
      .then((updated: any) => {
        saveScheduleData(scheduleData.id, updated);
        closeAddEditSchedule();
      })
      .catch((err: any) => addMessage({ message: err.message }))
      .finally(() => {
        setDisableAdding(false);
      });
  };

  const createSchedule = () => {
    const startHour = powerOnTime
      ? stringTimeToUTCMins(powerOnTime, is12Hours)
      : undefined;
    const endHour = powerOffTime
      ? stringTimeToUTCMins(powerOffTime, is12Hours)
      : undefined;

    const data: any = {
      isDisabled: scheduleDisabled,
      powerOnTime: startTimeActive ? startHour : null,
      powerOffTime: endTimeActive ? endHour : null,
      name: scheduleName,
      scheduleCategory: tab,
    };

    if (tab === scheduleCategories.calendar) {
      data.dates = dates;
    }
    if (tab === scheduleCategories.weekly) {
      data.days = days;
    }

    if (isSensor) {
      data.value = value;
      data.userMin = userMinMax ? userMinMax.userMin : undefined;
      data.userMax = userMinMax ? userMinMax.userMax : undefined;

      createSensorScheduleAPI({ sensorId: sensor.id, data })
        .then((newSchedule: any) => {
          addScheduleLocaly(newSchedule);
          closeAddEditSchedule();
        })
        .catch((err: any) => addMessage({ message: err.message }))
        .finally(() => {
          setDisableAdding(false);
        });
    } else {
      data.setpoint = setpointEnabled ? setpoint : undefined;
      data.fanMode = enableFanSpeed ? selectedFanSpeed : null;

      createUnitScheduleAPI({
        data,
        objId: itemId as string,
        objectType: isGroup ? 'group' : 'unit',
      })
        .then((newSchedule: any) => {
          addItemSchedule(isGroup, itemId, newSchedule.id);
          saveScheduleData(newSchedule.id, newSchedule);
          closeAddEditSchedule();
        })
        .catch((err: any) => addMessage({ message: err.message }))
        .finally(() => {
          setDisableAdding(false);
        });
    }
  };

  // Warns about BACnet/Modbus group members that can't carry out (some of) the schedule's actions
  const confirmUniversalMembersMapping = async () => {
    if (!isGroup || isSensor || !universalMembers.length) {
      return true;
    }
    const requiredProperties = getScheduleRequiredProperties({
      hasPowerTime: startTimeActive || endTimeActive,
      hasSetpoint: setpointEnabled,
      hasFanMode: enableFanSpeed,
    });
    const { excluded, partial } = getScheduleMappingIssues(
      universalMembers as UniversalGroupMember[],
      requiredProperties
    );
    if (!excluded.length && !partial.length) {
      return true;
    }
    const toBoldParts = (name: string, missing: UniversalProperty[]) => ({
      unitName: <strong key="name" className={classes.missingMappingBold}>{name}</strong>,
      missingPoints: <strong key="missing" className={classes.missingMappingBold}>{missing.map(getPropertyLabel).join(' / ')}</strong>,
    });
    const lines = [
      ...excluded.map(({ name, missing }) => {
        const { unitName, missingPoints } = toBoldParts(name, missing);
        return jt`${unitName} can not be part of the schedule as there is no proper mapping to ${missingPoints}.`;
      }),
      ...partial.map(({ name, missing }) => {
        const { unitName, missingPoints } = toBoldParts(name, missing);
        return jt`${unitName} has no mapping to ${missingPoints}, so these will not be part of the schedule.`;
      }),
    ];
    return confirm({
      title: t`Units With Missing Mapping`,
      bodyMessage: '',
      children: (
        <ul className={classes.missingMappingList}>
          {lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      ),
      confirmText: t`Save`,
      cancelText: t`Cancel`,
    });
  };

  const save = async () => {
    if (!isSensor) {
      const eventType = isGroup ? 'SaveGroupSchedule' : 'SaveUnitSchedule';
      const description = isGroup
        ? 'The User created a Group schedule'
        : 'The User created an IDU schedule';
      trackEvent(eventType, description);
    }

    const allRequiredNotEmpty = checkRequiredFields();

    if (!allRequiredNotEmpty) {
      return;
    }
    if (!(await confirmUniversalMembersMapping())) {
      return;
    }
    setDisableAdding(true);

    if (!scheduleData.id) {
      createSchedule();
      return;
    }

    editSchedule();
  };

  // ── Event handlers ────────────────────────────────────────────────────────
  const decreaseSetpoint = () => {
    if (setpoint < 1) {
      return;
    }
    setSetpoint(setpoint - changeScale);
    !dirty && setDirty(true);
  };

  const increaseSetpoint = () => {
    setSetpoint(setpoint + changeScale);
    !dirty && setDirty(true);
  };

  const addRemoveDay = (selectedDay: string) => {
    let currentDays: any = [...days];

    currentDays.includes(selectedDay)
      ? (currentDays = days.filter((day) => day !== selectedDay))
      : currentDays.push(selectedDay);

    setDays(currentDays);
    !dirty && setDirty(true);
  };

  const handleSetpoint = () => {
    enableSetpoint(!setpointEnabled);
    !dirty && setDirty(true);
  };

  const handleFanSpeed = () => {
    !enableFanSpeed && setSelectedFanSpeed(0);
    setEnableFanSpeed(!enableFanSpeed);
    !dirty && setDirty(true);
  };

  const changeScheduleStatus = () => {
    setScheduleStatus(!scheduleDisabled);
    !dirty && setDirty(true);
  };

  const currentDays: string[] = days;

  const onSetScheduleTime = (time: string) => {
    setScheduleTime(time);
    !dirty && setDirty(true);
  };

  const onSetOffTime = (time: string) => {
    setPowerOffTime(time);
    !dirty && setDirty(true);
  };

  const handleActiveChange = (pickerId: 'start' | 'end') => {
    !dirty && setDirty(true);
    switch (pickerId) {
      case 'start':
        setStartTimeActive((prev) => !prev);
        break;
      case 'end':
        setEndTimeActive((prev) => !prev);
        break;
      default:
        break;
    }
  };

  const handleDayClick = (date: any, { selected }: any) => {
    const selectedDates: any = dates.concat();
    if (selected) {
      const selectedIndex = selectedDates.findIndex((selectedDate: any) =>
        DateUtils.isSameDay(selectedDate, date)
      );
      selectedDates.splice(selectedIndex, 1);
    } else {
      selectedDates.push(date);
    }
    setDates(selectedDates);
    !dirty && setDirty(true);
  };


  const checkIfCanGoBack = async (goTo: any) => {
    if (editMode && schedule.name === '') {
      goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule();
      return;
    }
    if (
      (schedule.powerOnTime || schedule.powerOnTime === 0
        ? schedule.powerOnTime
        : '') !== stringTimeToUTCMins(powerOnTime, is12Hours)
    ) {
      const ok = await confirm({ title: t`Discard Changes`, bodyMessage: 'Are you sure you want to discard changes made on this page?', confirmText: t`Yes`, cancelText: t`No` });
      if (ok) { goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule(); }
      return;
    }
    if (
      (schedule.powerOffTime || schedule.powerOffTime === 0
        ? schedule.powerOffTime
        : '') !== stringTimeToUTCMins(powerOffTime, is12Hours)
    ) {
      const ok = await confirm({ title: t`Discard Changes`, bodyMessage: 'Are you sure you want to discard changes made on this page?', confirmText: t`Yes`, cancelText: t`No` });
      if (ok) { goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule(); }
      return;
    }
    if (schedule.isDisabled !== scheduleDisabled) {
      const ok = await confirm({
        title: t`Discard Changes`,
        bodyMessage:
          'Are you sure you want to discard changes made on this page?',
        confirmText: t`Yes`,
        cancelText: t`No`,
      });
      if (ok) {
        goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule();
      }
      return;
    }

    if (isSensor) {
      if (editMode ? +value !== +schedule.value : +mainValue !== value) {
        const ok = await confirm({
          title: t`Discard Changes`,
          bodyMessage:
            'Are you sure you want to discard changes made on this page?',
          confirmText: t`Yes`,
          cancelText: t`No`,
        });
        if (ok) { goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule(); }
        return;
      }
    } else {
      const currentSetpoint = isHalfCDegreeEnabled
        ? Math.floor(setpoint * 2) / 2
        : Math.round(setpoint);
      const oldSetpoint = isHalfCDegreeEnabled
        ? Math.floor(schedule.setpoint * 2) / 2
        : Math.round(schedule.setpoint);

      if (
        schedule.setpoint || schedule.setpoint === 0
          ? !setpointEnabled
            ? true
            : oldSetpoint !== currentSetpoint
          : setpointEnabled
      ) {
        const ok = await confirm({
          title: t`Discard Changes`,
          bodyMessage:
            'Are you sure you want to discard changes made on this page?',
          confirmText: t`Yes`,
          cancelText: t`No`,
        });
        if (ok) {
          goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule();
        }
        return;
      }
    }

    if (schedule.isDisabled !== scheduleDisabled) {
      const ok = await confirm({ title: t`Discard Changes`, bodyMessage: 'Are you sure you want to discard changes made on this page?', confirmText: t`Yes`, cancelText: t`No` });
      if (ok) { goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule(); }
      return;
    }
    if (!isEqual(schedule.days || [], days)) {
      const ok = await confirm({
        title: t`Discard Changes`,
        bodyMessage:
          'Are you sure you want to discard changes made on this page?',
        confirmText: t`Yes`,
        cancelText: t`No`,
      });
      if (ok) { goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule(); }
      return;
    }
    if (schedule.name !== scheduleName) {
      const ok = await confirm({
        title: t`Discard Changes`,
        bodyMessage:
          'Are you sure you want to discard changes made on this page?',
        confirmText: t`Yes`,
        cancelText: t`No`,
      });
      if (ok) {
        goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule();
      }
      return;
    }
    if (
      !isEqual(
        (schedule?.dates || []).map((date: any) => moment(date).unix()),
        dates.map((date: any) => moment(date).unix())
      )
    ) {
      const ok = await confirm({
        title: t`Discard Changes`,
        bodyMessage:
          'Are you sure you want to discard changes made on this page?',
        confirmText: t`Yes`,
        cancelText: t`No`,
      });
      if (ok) {
        goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule();
      }
      return;
    }

    goTo === 'close' ? hideSchedulesPanel() : closeAddEditSchedule();
  };

  const fanSpeedOptions = useMemo(() => (unit ? unit?.supportedFanModes : allFanModes)?.map((mode: any) => ({
    id: mode,
    label: types?.fanModesExtended[mode] || mode.toString(),
  })), [unit, allFanModes]);

  const showFanSpeed =
    !isSensor &&
    !enableCurveTempSelection &&
    (unit ? enableFanModeSelection : true) &&
    (fanSpeedOptions?.length ?? 0) > 0;

  const handleFanSpeedChange = (value: number) => {
    setSelectedFanSpeed(value);
    !dirty && setDirty(true);
  }

  const handleClose = () => {
    checkIfCanGoBack('back')
  }


  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <ScheduleWrapper onXClick={handleClose} isEditMode={editMode}>

      <ScheduleNameRow
        name={scheduleName}
        onNameChange={(newName: string) => {
          setScheduleName(newName);
          !dirty && setDirty(true);
        }}
        disabled={scheduleDisabled}
        onDisabledChange={changeScheduleStatus}
      />

      {/* Days Selection */}
      <DaysDatePicker
        tab={tab}
        onTabChange={setTab}
        days={days}
        onAddRemoveDay={addRemoveDay}
        dates={dates}
        onDayClick={handleDayClick}
        editMode={editMode}
      />

      <TimePickerRow
        id="time-on-picker"
        currentValue={scheduleTime}
        label={t`Schedule Time`}
        handleTimeChange={onSetScheduleTime}
        is12Hours={is12Hours}
        classes={classes}
        className={classes.mainTimeContainer}
      />

      <Typography className={clsx(classes.selectModeStyle, classes.selectModeLabel, classes.actionsLabel)}>
        {t`Actions`}
      </Typography>

      <CheckboxRow
        title={t`Turn On`}
        checked={startTimeActive}
        onChange={() => handleActiveChange('start')}
        classes={classes}
      />

      <TimePickerRow
        id="time-off-picker"
        currentValue={powerOffTime}
        isActive={endTimeActive}
        handleActiveChange={() => handleActiveChange('end')}
        label={t`Turn Off`}
        handleTimeChange={onSetOffTime}
        is12Hours={is12Hours}
        hideTimePicker={!startTimeActive}
        classes={classes}
        className={classes.startEndTimeContainer}
      />


      {/* Sensor-specific: circular slider or enum switch for value */}
      {isSensor && !isOnOff && (
        <Grid
          container
          className={clsx(classes.startEndTimeContainer, classes.container)}
        >
          <Typography
            className={classes.selectModeStyle}
          >{t`Select Value`}</Typography>
          <div className={classes.selectModeContainer}>
            <Grid className={classes.setpointContainer}>
              {sensorType === 130 ? (
                <div className={classes.sensorSliderContainer}>
                  <CircularSlider
                    size={275}
                    minValue={
                      userMinMax ? userMinMax.userMin : valueMinMax.min
                    }
                    maxValue={
                      userMinMax ? userMinMax.userMax : valueMinMax.max
                    }
                    startAngle={0}
                    endAngle={180}
                    coerceToInt={true}
                    angleType={{ direction: 'cw', axis: '-x' }}
                    handle1={{
                      value,
                      onChange: (v: any) => {
                        setValue(v);
                        !dirty && setDirty(true);
                      },
                    }}
                    arcColor={'#FFF'}
                    arcBackgroundColor={'#FFF'}
                  />
                  <div className={classes.hideHalfOfCircle}></div>
                  <div className={classes.sensorValuesContainer}>
                    <Typography className={classes.sensorValues}>
                      {userMinMax ? userMinMax.userMin : valueMinMax.min}
                    </Typography>
                    <Typography className={classes.sensorValues}>
                      {userMinMax ? userMinMax.userMax : valueMinMax.max}
                    </Typography>
                  </div>
                  <div className={classes.valueControl}>
                    <Typography className={classes.sensorValue}>
                      {value}
                    </Typography>
                    <Typography className={classes.measurementUnit}>
                      {symbols[sensorUnit || ''] || ''}
                    </Typography>
                  </div>
                </div>
              ) : sensorEnums ? (
                <div className={classes.enumContainer}>
                  <Typography style={{ marginRight: 10 }}>
                    {sensorEnums[0]}
                  </Typography>
                  <GreenSwitch
                    checked={value}
                    disableRipple={true}
                    onChange={(event: any) => {
                      setValue(event.target.checked ? 1 : 0);
                      !dirty && setDirty(true);
                    }}
                    value={true}
                    color={theme.palette.colors?.switchPower?.colorChecked || theme.palette.colors?.switch?.colorChecked}
                    uncheckedColor={theme.palette.colors?.switchPower?.color || theme.palette.colors?.switch?.color}
                  />
                  <Typography style={{ marginLeft: 10 }}>
                    {sensorEnums[1]}
                  </Typography>
                </div>
              ) : null}
            </Grid>
          </div>
        </Grid>
      )}

      {/* Unit-specific: setpoint */}
      {!isSensor && (unit ? enableSetpointControl : true) && (
        <Grid container className={clsx(classes.container, classes.setPointContainer)}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CustomCheckbox
              onChange={handleSetpoint}
              checked={setpointEnabled}
            />
            <Typography
              className={classes.selectModeStyle}
            >{t`Setpoint`}</Typography>
          </div>

          <SetpointPicker
            disabled={!setpointEnabled}
            onIncrease={increaseSetpoint}
            onDecrease={decreaseSetpoint}
            value={
              setpointEnabled
                ? isHalfCDegreeEnabled
                  ? Math.floor(setpoint * 2) / 2
                  : Math.round(setpoint)
                : temperatureScale === 1
                  ? '24'
                  : '75'
            }
            unit={temperatureSymbol()}
            className={clsx({ [classes.hide]: !setpointEnabled })}
          />

        </Grid>
      )}

      {/* Unit-specific: fan speed selection */}
      {showFanSpeed && (
        <Grid container className={clsx(classes.container, classes.setPointContainer)}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CustomCheckbox
              onChange={handleFanSpeed}
              checked={enableFanSpeed}
            />
            <Typography
              className={classes.selectModeStyle}
            >{t`Fan Speed`}</Typography>
          </div>

          {enableFanSpeed && <FanSpeedPicker
            value={selectedFanSpeed}
            options={fanSpeedOptions}
            onChange={handleFanSpeedChange}
          />}
        </Grid>
      )}

      {/* Unit-specific: curve temp selection */}
      {!isSensor && unit && enableCurveTempSelection && (
        <Grid container className={clsx(classes.container)}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              className={classes.selectModeStyle}
            >{t`Select Curve Temp`}</Typography>
            <Checkbox
              onChange={handleFanSpeed}
              checked={enableFanSpeed}
              className={classes.checkboxStyle}
            />
          </div>

          <div
            className={clsx(
              classes.selectModeContainer,
              !enableFanSpeed && classes.collapse
            )}
          >
            <div
              style={{
                display: !enableFanSpeed ? 'none' : 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                alignItems: 'center',
                flex: 1,
              }}
            >
              {Object.keys(
                unit?.supportedcurveTempModes || curveTempModes
              )?.map((mode: string, i: number) => {
                const icon: any = <CurveTempIcon i={+mode} />;
                // tslint:disable-next-line: triple-equals
                const isSelected =
                  !isNaN(selectedFanSpeed) && selectedFanSpeed == mode;
                return (
                  <IconButton
                    key={`mode-${i}`}
                    style={{
                      width: '55px',
                      height: '55px',
                      backgroundColor: isSelected ? theme.palette.primary.main : '#fff',
                      margin: '7px',
                    }}
                    onClick={() => {
                      setSelectedFanSpeed(+mode);
                      !dirty && setDirty(true);
                    }}
                  >
                    {icon &&
                      React.cloneElement(
                        icon,
                        isSelected
                          ? { opacity: 0.6 }
                          : {
                            colorblue: '#35A8E0',
                            color1: theme.palette.primary.main,
                            color2: '#AAA2AA',
                            maincolor: '#AAA2AA',
                            opacity: 1,
                          }
                      )}
                  </IconButton>
                );
              })}
            </div>
          </div>
        </Grid>
      )}

      <ScheduleSummary
        powerOnTime={powerOnTime}
        powerOffTime={powerOffTime}
        startTimeActive={startTimeActive}
        endTimeActive={endTimeActive}
        setpoint={setpoint}
        setpointEnabled={setpointEnabled}
        temperatureSymbol={temperatureSymbol()}
        is12Hours={is12Hours}
      />

      {errorMsg && (
        <InputLabel className={classes.errorLabelStyle}>
          {errorMsg}
        </InputLabel>
      )}

      <div className={classes.actionsContainer}>
        <Button
          disableElevation
          disableRipple
          variant="text"
          onClick={handleClose}
          className={classes.cancelButton}
        >
          {t`Cancel`}
        </Button>
        <Button
          disableElevation
          disableRipple
          type="submit"
          variant="contained"
          onClick={save}
          disabled={!dirty}
          className={classes.redBtn}
        >
          {t`Save`}
        </Button>
      </div>


    </ScheduleWrapper>
  );
};

export default AddEditSchedule;
